import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Package, MapPin, Calendar, Shield, CheckCircle, Leaf, AlertCircle, Mail, Share2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import SustainabilityBadge from '../components/common/SustainabilityBadge';
import BatchTimeline from '../components/traceability/BatchTimeline';
import OptimarVerify from './optimar/OptimarVerify';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Batch IDs that should render the Optimar-inspired demo view instead of
// the standard PureSustain verification flow. Public route — no auth.
const OPTIMAR_DEMO_BATCH_IDS = new Set(['OPT-DEMO-001']);

const VerifyBatch = () => {
  const { id } = useParams();

  // If the QR/customer scanned an Optimar demo batch ID, render the
  // limited Optimar customer-facing verify view.
  if (id && OPTIMAR_DEMO_BATCH_IDS.has(id)) {
    return <OptimarVerify batchId={id} />;
  }

  return <PureSustainVerify id={id} />;
};

const PureSustainVerify = ({ id }) => {
  const [batch, setBatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBatchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBatchData = async () => {
    try {
      const [batchRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/batches/${id}`),
        axios.get(`${BACKEND_URL}/api/batches/${id}/events`),
      ]);
      setBatch(batchRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching batch:', error);
      setError('Batch not found or verification failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Verification link copied to clipboard!');
  };

  const handleEmailShare = () => {
    const verifyUrl = window.location.href;
    const subject = encodeURIComponent(`PureSustain Batch Verification: ${batch.batch_number}`);
    const body = encodeURIComponent(
      `I'm sharing the complete supply chain verification for:\n\n` +
      `Product: ${batch.product_name}\n` +
      `Batch: ${batch.batch_number}\n` +
      `Origin: ${batch.origin.name}, ${batch.origin.region}\n` +
      `Integrity Score: ${batch.integrity_score}/100\n\n` +
      `View full verification:\n${verifyUrl}\n\n` +
      `Verified by PureSustain - Supply Chain Transparency`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Verifying batch...</p>
        </div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-white rounded-xl border border-slate-200">
          <AlertCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
          <p className="text-slate-600">{error || 'Batch not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verify-batch-page">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-6 sm:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                Verified by PureSustain
              </h1>
              <p className="text-blue-100 text-xs sm:text-sm">Supply Chain Transparency</p>
            </div>
          </div>
          <div className="bg-blue-500/30 backdrop-blur-sm rounded-lg px-4 py-2 inline-block">
            <p className="text-xs sm:text-sm text-blue-50">
              ⚠️ Demonstration Environment - Proof of Concept Data
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 sm:py-12 space-y-6 sm:space-y-8">
        <div className="bg-white rounded-xl p-5 sm:p-8 border border-slate-200 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 shrink-0" />
                <span className="text-sm sm:text-base font-mono text-slate-600 truncate">{batch.batch_number}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold leading-tight text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
                {batch.product_name}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 italic">{batch.species}</p>

              {/* Share Buttons */}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  size="sm"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={handleEmailShare}
                  variant="outline"
                  size="sm"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Share via Email
                </Button>
              </div>
            </div>
            <div className="text-center sm:text-right">
              {batch.blockchain_verified && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-300 rounded-lg mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-700" />
                  <span className="text-sm font-medium text-emerald-800">Blockchain-Verified</span>
                </div>
              )}
              <TooltipProvider>
                <div>
                  <div className="flex items-center gap-2 justify-center sm:justify-end mb-1">
                    <span className="text-4xl font-bold text-slate-800">{batch.integrity_score}</span>
                    <span className="text-slate-600">/100</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">
                          Integrity Score reflects data completeness, event verification, and traceability continuity within the PureSustain platform. The score may be affected by missing data, delayed events, or manual overrides. This score is not a certification or regulatory guarantee.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-sm text-slate-500">Integrity Score</p>
                  <p className="text-xs text-slate-400 mt-1">Based on data completeness and verification</p>
                </div>
              </TooltipProvider>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pt-6 border-t border-slate-100">
            <div>
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <MapPin className="w-4 h-4 shrink-0" />
                <span className="text-xs uppercase tracking-wider">Origin</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-slate-900 break-words">{batch.origin.name}</p>
              <p className="text-sm text-slate-500">{batch.origin.region}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Calendar className="w-4 h-4 shrink-0" />
                <span className="text-xs uppercase tracking-wider">Catch Date</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-slate-900">{formatDate(batch.catch_date)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Package className="w-4 h-4 shrink-0" />
                <span className="text-xs uppercase tracking-wider">Weight</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-slate-900">{batch.weight_kg.toLocaleString()} kg</p>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2 text-slate-600 mb-1">
                <Leaf className="w-4 h-4 shrink-0" />
                <span className="text-xs uppercase tracking-wider">CO₂ Footprint</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs cursor-help">
                        Est.
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Data provenance: Estimated (PoC)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-sm sm:text-base font-semibold text-slate-900">{batch.total_co2_kg} kg</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 sm:p-8 border border-slate-200 shadow-sm">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4" style={{ fontFamily: 'Manrope' }}>
            Compliance Status
          </h3>
          <div className="flex flex-wrap gap-2">
            {batch.compliance.map((comp, idx) => (
              <SustainabilityBadge
                key={idx}
                standard={comp.standard}
                status={comp.status}
                description={comp.description}
                lastAudit={comp.last_audit}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 italic">
            Compliance status reflects data completeness, traceability, and verification within the PureSustain platform. Regulatory certification remains the responsibility of accredited authorities.
          </p>
        </div>

        <div className="bg-white rounded-xl p-5 sm:p-8 border border-slate-200 shadow-sm">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
            Chain of Custody
          </h3>
          <BatchTimeline events={events} />
        </div>

        <div className="text-center py-6 sm:py-8 border-t border-slate-200">
          <p className="text-sm text-slate-500 mb-2">
            All displayed data is read-only and immutably recorded. External viewers do not modify, approve, or certify records.
          </p>
          <p className="text-xs text-slate-400">
            Generated by PureSustain platform for traceability and reporting support. This document does not replace regulatory filings or accredited certification.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyBatch;
